import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useOfflineGuard } from '@/hooks/useOfflineGuard';
import { supabase } from '@/lib/supabase';
import {
  LiveKitRoom,
  GridLayout,
  ParticipantTile,
  ControlBar,
  RoomAudioRenderer,
  useParticipants,
  useTracks,
} from '@livekit/components-react';
import '@livekit/components-styles';
import type { Participant, TrackReference } from 'livekit-client';
import { Track } from 'livekit-client';
import RaiseHandControls from './components/RaiseHandControls';

interface DbParticipant {
  id: string;
  user_id: string;
  participant_name: string;
  joined_at: string;
  left_at: string | null;
}

interface MeetingData {
  id: string;
  code: string;
  title: string;
  host_name: string;
  created_by: string;
  created_at: string;
  is_active: boolean;
  scheduled_at: string | null;
}

function ParticipantsPanel({
  dbParticipants,
  livekitParticipants,
  meeting,
  code,
  onCopyCode,
  copied,
  onClose,
  isMobile,
}: {
  dbParticipants: DbParticipant[];
  livekitParticipants: Participant[];
  meeting: MeetingData | null;
  code: string | undefined;
  onCopyCode: () => void;
  copied: boolean;
  onClose?: () => void;
  isMobile?: boolean;
}) {
  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const livekitIdentitySet = new Set(livekitParticipants.map((p) => p.identity));
  const getPresentCount = () => dbParticipants.filter((p) => !p.left_at).length;
  const getTotalCount = () => dbParticipants.length;

  return (
    <div className={`${isMobile ? '' : 'w-72'} flex-shrink-0 bg-white border-l border-gray-200 flex flex-col h-full`}>
      <div className="flex-shrink-0 px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
          <i className="ri-group-line text-gray-500"></i>
          Participants
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
            {getPresentCount()}/{getTotalCount()}
          </span>
          {isMobile && onClose && (
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center cursor-pointer"
            >
              <i className="ri-close-line text-gray-500"></i>
            </button>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-2">
        {dbParticipants.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <i className="ri-user-line text-gray-400"></i>
            </div>
            <p className="text-xs text-gray-500">En attente de participants...</p>
          </div>
        ) : (
          <div className="space-y-1">
            {dbParticipants.map((p) => {
              const isLive = livekitIdentitySet.has(p.user_id);
              const isGuest = p.user_id.startsWith('guest_');
              return (
                <div
                  key={p.id}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                    p.left_at ? 'opacity-50' : 'bg-gray-50'
                  }`}
                >
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                      p.left_at
                        ? 'bg-gray-200'
                        : p.user_id === meeting?.created_by
                          ? 'bg-clinic-green/15'
                          : isGuest
                            ? 'bg-amber-50'
                            : 'bg-clinic-blue/10'
                    }`}
                  >
                    <span
                      className={`text-xs font-semibold ${
                        p.left_at
                          ? 'text-gray-500'
                          : p.user_id === meeting?.created_by
                            ? 'text-clinic-green'
                            : isGuest
                              ? 'text-amber-600'
                              : 'text-clinic-blue'
                      }`}
                    >
                      {p.participant_name
                        ? p.participant_name
                            .replace(' (invité)', '')
                            .split(' ')
                            .map((n) => n[0])
                            .join('')
                            .substring(0, 2)
                            .toUpperCase()
                        : '??'}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {p.participant_name.replace(' (invité)', '')}
                      {p.user_id === meeting?.created_by && (
                        <span className="ml-1.5 text-xs text-clinic-green font-normal">(Hôte)</span>
                      )}
                      {isGuest && p.user_id !== meeting?.created_by && (
                        <span className="ml-1.5 text-xs text-amber-500 font-normal">(Invité)</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      {p.left_at
                        ? `Parti à ${formatTime(p.left_at)}`
                        : isLive
                          ? 'En ligne'
                          : `Présent depuis ${formatTime(p.joined_at)}`}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <span
                      className={`w-2 h-2 rounded-full block ${
                        p.left_at ? 'bg-gray-400' : isLive ? 'bg-green-500 animate-pulse' : 'bg-yellow-400'
                      }`}
                    ></span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="flex-shrink-0 px-4 py-3 border-t border-gray-100 bg-gray-50/50">
        <p className="text-xs text-gray-500 mb-1.5">Code de la réunion</p>
        <button
          onClick={onCopyCode}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white border border-gray-200 hover:border-gray-300 rounded-lg transition-colors cursor-pointer group"
        >
          <span className="text-sm font-mono font-bold text-gray-800 tracking-widest">{code}</span>
          <i className="ri-file-copy-line text-gray-400 group-hover:text-gray-600 text-sm"></i>
        </button>
      </div>
    </div>
  );
}

export default function ReunionPage() {
  const { code } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();

  const { user, role, userRoles, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // ⭐ Déterminer le mode : invité (guest) ou staff (authentifié)
  const [isGuest, setIsGuest] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [nameSubmitted, setNameSubmitted] = useState(false);
  const [guestId, setGuestId] = useState('');

  const [meeting, setMeeting] = useState<MeetingData | null>(null);
  const [isHost, setIsHost] = useState(false);
  const [dbParticipants, setDbParticipants] = useState<DbParticipant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [meetingEnded, setMeetingEnded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showParticipantsMobile, setShowParticipantsMobile] = useState(false);

  const offlineGuard = useOfflineGuard('video_meetings', 'write');

  const [livekitToken, setLivekitToken] = useState('');
  const [livekitUrl, setLivekitUrl] = useState('');
  const [lkConnected, setLkConnected] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const leftRef = useRef(false);

  // Verrouiller le scroll
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, []);

  // Wake Lock
  useEffect(() => {
    let wakeLock: any = null;
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await (navigator as any).wakeLock.request('screen');
          wakeLock.addEventListener('release', () => {
            if (!meetingEnded) requestWakeLock();
          });
        }
      } catch { /* pas supporté */ }
    };
    if (livekitToken && !meetingEnded) requestWakeLock();
    return () => { if (wakeLock) wakeLock.release().catch(() => {}); };
  }, [livekitToken, meetingEnded]);

  // ⭐ Décider du mode : invité ou staff
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      // Pas connecté → mode invité
      setIsGuest(true);
      setLoading(true);
      checkMeetingInfo();
      return;
    }
    // Connecté mais patient → rediriger
    if (role === 'patient' || userRoles.includes('patient')) {
      navigate('/', { replace: true });
      return;
    }
    // Staff authentifié → rejoindre normalement
    setIsGuest(false);
    joinAsStaff();
  }, [user, role, userRoles, authLoading, code]);

  // ⭐ Vérifier que la réunion existe (pour les invités)
  const checkMeetingInfo = async () => {
    setLoading(true);
    setError('');
    try {
      const url = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-video-meeting`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'meeting-info', code: code?.toUpperCase().trim() }),
      });
      const data = await resp.json();
      if (data?.error) {
        setError(data.error);
        setLoading(false);
        return;
      }
      setMeeting(data.meeting);
      setLoading(false);
    } catch {
      setError('Impossible de vérifier la réunion.');
      setLoading(false);
    }
  };

  // ⭐ Staff : rejoindre via l'action authentifiée
  const joinAsStaff = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fnError } = await supabase.functions.invoke('manage-video-meeting', {
        body: { action: 'join-meeting', code: code?.toUpperCase().trim() },
      });

      if (fnError || data?.error) {
        setError(data?.error || fnError?.message || 'Erreur de connexion.');
        setLoading(false);
        return;
      }

      if (!data?.livekitToken) {
        setError('Impossible de se connecter au service vidéo.');
        setLoading(false);
        return;
      }

      setMeeting(data.meeting);
      setIsHost(data.isHost);
      setDbParticipants(data.participants || []);
      setLivekitToken(data.livekitToken);
      setLivekitUrl(import.meta.env.VITE_PUBLIC_LIVEKIT_URL || '');
      setNameSubmitted(true);
      setLoading(false);
    } catch {
      setError('Erreur lors de la connexion à la réunion.');
      setLoading(false);
    }
  };

  // ⭐ Invité : rejoindre avec le nom saisi
  const handleGuestSubmitName = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = guestName.trim();
    if (!name || name.length < 1) return;

    setLoading(true);
    setError('');

    try {
      const url = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-video-meeting`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'join-public-meeting', code: code?.toUpperCase().trim(), guest_name: name }),
      });
      const data = await resp.json();

      if (data?.error) {
        setError(data.error);
        setLoading(false);
        return;
      }

      setMeeting(data.meeting);
      setDbParticipants(data.participants || []);
      setLivekitToken(data.livekitToken);
      setLivekitUrl(import.meta.env.VITE_PUBLIC_LIVEKIT_URL || '');
      setGuestId(data.participant?.guestId || '');
      setIsHost(false);
      setNameSubmitted(true);
      setLoading(false);
    } catch {
      setError('Erreur lors de la connexion à la réunion.');
      setLoading(false);
    }
  };

  // Polling des participants
  useEffect(() => {
    if (!meeting || meetingEnded || !nameSubmitted) return;

    const fetchParticipants = async () => {
      try {
        if (isGuest) {
          // Invité : utiliser meeting-status (public)
          const url = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-video-meeting`;
          const resp = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'meeting-status', code: meeting.code }),
          });
          const data = await resp.json();
          if (data?.participants) setDbParticipants(data.participants);
          if (data?.isActive === false) setMeetingEnded(true);
        } else {
          // Staff : utiliser meeting-participants (auth)
          const { data } = await supabase.functions.invoke('manage-video-meeting', {
            body: { action: 'meeting-participants', code: meeting.code },
          });
          if (data?.participants) setDbParticipants(data.participants);
          if (data?.isActive === false) setMeetingEnded(true);
        }
      } catch { /* silencieux */ }
    };

    pollRef.current = setInterval(fetchParticipants, 8000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [meeting, meetingEnded, nameSubmitted, isGuest]);

  // Cleanup au démontage
  useEffect(() => {
    return () => {
      if (!meeting?.code || leftRef.current) return;
      leftRef.current = true;

      if (isGuest && guestId) {
        // Invité : sendBeacon garanti
        const url = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-video-meeting`;
        const body = new Blob([JSON.stringify({ action: 'leave-public-meeting', code: meeting.code, guest_id: guestId })], { type: 'application/json' });
        navigator.sendBeacon(url, body);
      } else if (!isGuest && isHost) {
        // ⭐ Hôte : end-meeting pour tout le monde
        const url = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-video-meeting`;
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'leave-meeting', code: meeting.code }),
          keepalive: true,
        }).catch(() => {});
      } else if (!isGuest && !isHost) {
        // Staff non-hôte : leave normal
        const url = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-video-meeting`;
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'leave-meeting', code: meeting.code }),
          keepalive: true,
        }).catch(() => {});
      }
    };
  }, [meeting, isGuest, guestId, isHost]);

  // ⭐ Hôte : terminer la réunion (clic "Terminer" ou "Quitter")
  const handleEndMeeting = async () => {
    if (!meeting || !isHost) return;
    try {
      await supabase.functions.invoke('manage-video-meeting', {
        body: { action: 'end-meeting', code: meeting.code },
      });
      setMeetingEnded(true);
    } catch { /* silencieux */ }
  };

  // ⭐ Quitter (non-hôte staff ou invité)
  const handleLeaveMeeting = useCallback(() => {
    if (!meeting?.code || leftRef.current) return;
    leftRef.current = true;

    if (isGuest && guestId) {
      const url = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-video-meeting`;
      const body = new Blob([JSON.stringify({ action: 'leave-public-meeting', code: meeting.code, guest_id: guestId })], { type: 'application/json' });
      navigator.sendBeacon(url, body);
    } else if (!isGuest && isHost) {
      // ⭐ L'hôte clique "Quitter" → fin de réunion
      const url = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-video-meeting`;
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'end-meeting', code: meeting.code }),
        keepalive: true,
      }).catch(() => {});
      // Pas besoin d'attendre, on navigue
    } else {
      const url = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-video-meeting`;
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'leave-meeting', code: meeting.code }),
        keepalive: true,
      }).catch(() => {});
    }

    navigate(isGuest ? '/' : '/visioconference');
  }, [meeting, isGuest, guestId, isHost, navigate]);

  const handleCopyLink = () => {
    const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
    const pathPrefix = basePath ? `/${basePath}` : '';
    const link = `${window.location.origin}${pathPrefix}/visioconference/reunion/${code}`;
    navigator.clipboard.writeText(link).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 2000); },
      () => {
        const input = document.createElement('input');
        input.value = link;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      },
    );
  };

  const handleCopyCode = () => {
    if (!code) return;
    navigator.clipboard.writeText(code).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 2000); },
      () => {},
    );
  };

  const getDbPresentCount = () => dbParticipants.filter((p) => !p.left_at).length;

  // ⭐ Écran : chargement auth
  if (authLoading) {
    return (
      <div className="w-full h-[100dvh] flex flex-col items-center justify-center bg-white">
        <div className="w-16 h-16 rounded-2xl bg-clinic-blue/10 flex items-center justify-center mb-4">
          <i className="ri-loader-4-line animate-spin text-3xl text-clinic-blue"></i>
        </div>
        <p className="text-gray-500 text-sm">Chargement...</p>
      </div>
    );
  }

  // Patient → déjà redirigé
  if (user && (role === 'patient' || userRoles.includes('patient'))) return null;

  // Barre supérieure commune
  const TopBar = ({ children }: { children?: React.ReactNode }) => (
    <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-2.5">
      <div className="flex items-center gap-3">
        <button
          onClick={handleLeaveMeeting}
          className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors flex-shrink-0 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-gray-700 text-lg"></i>
        </button>
        <h1 className="text-sm font-semibold text-clinic-blue-dark truncate">
          {meeting?.title || 'Réunion'}
        </h1>
        {children}
      </div>
    </div>
  );

  // ⭐ Écran : saisie du nom (invité, pas encore rejoint)
  if (isGuest && !nameSubmitted) {
    return (
      <div className="w-full h-[100dvh] flex flex-col bg-white overflow-hidden">
        <TopBar />
        <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-6">
          {loading ? (
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-clinic-green/10 flex items-center justify-center mx-auto mb-4">
                <i className="ri-loader-4-line animate-spin text-3xl text-clinic-green"></i>
              </div>
              <p className="text-gray-600 text-sm">Vérification de la réunion...</p>
            </div>
          ) : error ? (
            <div className="text-center max-w-sm">
              <div className="w-20 h-20 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-5">
                <i className="ri-error-warning-line text-4xl text-red-400"></i>
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Réunion inaccessible</h2>
              <p className="text-gray-600 text-sm mb-6">{error}</p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={checkMeetingInfo}
                  className="px-5 py-2.5 bg-clinic-blue hover:bg-clinic-blue/90 text-white rounded-xl transition-colors font-medium text-sm flex items-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line"></i>
                  Réessayer
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="px-5 py-2.5 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium text-sm whitespace-nowrap cursor-pointer"
                >
                  Retour
                </button>
              </div>
            </div>
          ) : meeting ? (
            <div className="text-center max-w-sm w-full">
              <div className="w-20 h-20 bg-gradient-to-br from-clinic-green/15 to-clinic-green/5 rounded-3xl flex items-center justify-center mx-auto mb-5">
                <i className="ri-vidicon-line text-4xl text-clinic-green"></i>
              </div>
              <h1 className="text-xl md:text-2xl font-bold text-clinic-blue-dark mb-1">
                {meeting.title}
              </h1>
              <p className="text-gray-500 text-sm mb-2">
                Organisée par {meeting.host_name}
              </p>
              <p className="text-xs text-gray-400 mb-8">
                Entrez votre nom pour rejoindre la visioconférence
              </p>

              <form onSubmit={handleGuestSubmitName} className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                  Votre nom
                </label>
                <input
                  type="text"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Ex: Dr. Martin"
                  maxLength={50}
                  className="w-full px-4 py-3.5 text-base bg-gray-50 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-clinic-green/40 transition-colors text-center"
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={!guestName.trim()}
                  className="w-full mt-4 px-6 py-3.5 bg-clinic-green hover:bg-clinic-green-dark text-white rounded-2xl transition-all font-semibold text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-login-circle-line text-lg"></i>
                  Rejoindre la réunion
                </button>
              </form>

              <p className="mt-6 text-xs text-gray-400">
                En rejoignant, votre micro sera activé. Vous pourrez activer votre caméra dans la réunion.
              </p>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  // ⭐ Écran : chargement/erreur avant connexion LiveKit
  if (!livekitToken && !meetingEnded) {
    if (offlineGuard.isBlocked) {
      return (
        <div className="w-full h-[100dvh] flex flex-col bg-white overflow-hidden">
          <TopBar />
          <div className="flex-1 flex flex-col items-center justify-center bg-gray-900 p-4 min-h-0">
            <div className="w-20 h-20 rounded-2xl bg-amber-500/20 flex items-center justify-center mb-5">
              <i className="ri-wifi-off-line text-4xl text-amber-400"></i>
            </div>
            <h2 className="text-white text-lg font-semibold mb-2">Connexion requise</h2>
            <p className="text-white/60 text-sm text-center max-w-md mb-6">
              {offlineGuard.blockMessage || 'La visioconférence nécessite une connexion internet.'}
            </p>
            <button
              onClick={handleLeaveMeeting}
              className="px-6 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors font-medium text-sm whitespace-nowrap cursor-pointer"
            >
              {isGuest ? 'Retour' : 'Retour au lobby'}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="w-full h-[100dvh] flex flex-col bg-white overflow-hidden">
        <TopBar />
        <div className="flex-1 flex flex-col items-center justify-center bg-gray-900 p-4 min-h-0">
          {loading ? (
            <>
              <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mb-4">
                <i className="ri-loader-4-line animate-spin text-3xl text-white"></i>
              </div>
              <p className="text-white/70 text-sm">Connexion à la réunion...</p>
              <p className="text-white/40 text-xs mt-1">Établissement de la liaison vidéo sécurisée</p>
            </>
          ) : error ? (
            <>
              <div className="w-20 h-20 rounded-2xl bg-red-500/20 flex items-center justify-center mb-5">
                <i className="ri-error-warning-line text-4xl text-red-400"></i>
              </div>
              <h2 className="text-white text-lg font-semibold mb-2">Accès impossible</h2>
              <p className="text-white/60 text-sm text-center max-w-md mb-6">{error}</p>
              <div className="flex gap-3">
                <button
                  onClick={isGuest ? checkMeetingInfo : joinAsStaff}
                  className="px-5 py-2.5 bg-clinic-blue hover:bg-clinic-blue/90 text-white rounded-xl transition-colors font-medium text-sm whitespace-nowrap flex items-center gap-2 cursor-pointer"
                >
                  <i className="ri-refresh-line"></i>
                  Réessayer
                </button>
                <button
                  onClick={handleLeaveMeeting}
                  className="px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors font-medium text-sm whitespace-nowrap cursor-pointer"
                >
                  {isGuest ? 'Retour' : 'Retour au lobby'}
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    );
  }

  // ⭐ Réunion terminée
  if (meetingEnded) {
    return (
      <div className="w-full h-[100dvh] flex flex-col bg-white overflow-hidden">
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-2.5">
          <div className="flex items-center gap-3">
            <button
              onClick={handleLeaveMeeting}
              className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors flex-shrink-0 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-gray-700 text-lg"></i>
            </button>
            <h1 className="text-sm font-semibold text-clinic-blue-dark">{meeting?.title || 'Réunion'}</h1>
          </div>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center bg-gray-900 p-4 min-h-0">
          <div className="w-20 h-20 rounded-2xl bg-clinic-green/20 flex items-center justify-center mb-5">
            <i className="ri-check-line text-4xl text-clinic-green"></i>
          </div>
          <h2 className="text-white text-lg font-semibold mb-2">Réunion terminée</h2>
          <p className="text-white/60 text-sm text-center max-w-md mb-6">
            {isHost
              ? 'Vous avez terminé la réunion. Merci à tous les participants !'
              : 'La réunion a été terminée par l\'hôte. Merci de votre participation !'}
          </p>
          <button
            onClick={handleLeaveMeeting}
            className="px-6 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors font-medium text-sm whitespace-nowrap cursor-pointer"
          >
            {isGuest ? 'Retour' : 'Retour au lobby'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <LiveKitRoom
      token={livekitToken}
      serverUrl={livekitUrl}
      connect={true}
      video={false}
      audio={true}
      onConnected={() => setLkConnected(true)}
      onDisconnected={() => {
        if (!meetingEnded) setMeetingEnded(true);
      }}
      className="w-full h-[100dvh] flex flex-col overflow-hidden bg-gray-900"
      style={{
        '--lk-bg': '#111827',
        '--lk-control-bg': 'rgba(255, 255, 255, 0.08)',
        '--lk-control-hover-bg': 'rgba(255, 255, 255, 0.16)',
        '--lk-control-active-bg': 'rgba(255, 255, 255, 0.22)',
        '--lk-control-fg': 'rgba(255, 255, 255, 0.9)',
        '--lk-accent': '#8DC93F',
        '--lk-accent-fg': '#ffffff',
        '--lk-danger': '#ef4444',
        '--lk-danger-fg': '#ffffff',
        '--lk-control-bar-bg': 'rgba(17, 24, 39, 0.95)',
        '--lk-control-bar-border': 'rgba(255, 255, 255, 0.08)',
        '--lk-fg': 'rgba(255, 255, 255, 0.9)',
      } as React.CSSProperties}
    >
      {/* Barre supérieure */}
      <div className="flex-shrink-0 bg-gray-900/95 border-b border-white/10 px-4 py-2.5 backdrop-blur-sm">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={isHost ? handleEndMeeting : handleLeaveMeeting}
              className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors flex-shrink-0 cursor-pointer"
              title={isHost ? 'Terminer la réunion' : 'Quitter la réunion'}
            >
              <i className="ri-arrow-left-line text-white/80 text-lg"></i>
            </button>
            <div className="min-w-0">
              <h1 className="text-sm font-semibold text-white truncate max-w-[200px] md:max-w-xs">
                {meeting?.title || 'Réunion'}
              </h1>
              <p className="text-xs text-white/60 flex items-center gap-2">
                <span className="flex items-center gap-1">
                  <span className={`w-1.5 h-1.5 rounded-full ${getDbPresentCount() > 0 ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`}></span>
                  {lkConnected ? `${getDbPresentCount()} présent${getDbPresentCount() !== 1 ? 's' : ''}` : 'Connexion...'}
                </span>
                {isHost && <span className="text-clinic-green font-medium">· Hôte</span>}
                {isGuest && <span className="text-amber-400 font-medium">· Invité</span>}
              </p>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={handleCopyCode}
              className="flex items-center gap-2 px-4 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-colors cursor-pointer group"
            >
              <span className="text-sm font-mono font-bold text-white/80 tracking-widest">{code}</span>
              <i className={`${copied ? 'ri-check-line text-green-400' : 'ri-file-copy-line text-white/40 group-hover:text-white/70'} text-sm`}></i>
            </button>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setShowParticipantsMobile(!showParticipantsMobile)}
              className="md:hidden flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-xs font-medium text-white/80 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-group-line"></i>
              <span>{getDbPresentCount()}</span>
            </button>

            {!isGuest && (
              <RaiseHandControls
                meetingCode={code || ''}
                userId={user?.id || ''}
                userName={
                  user?.user_metadata
                    ? `${user.user_metadata.prenom || ''} ${user.user_metadata.nom || ''}`.trim() || user.email || ''
                    : user?.email || ''
                }
                isHost={isHost}
              />
            )}

            <button
              onClick={handleCopyLink}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-xs font-medium text-white/80 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className={`${copied ? 'ri-check-line text-green-400' : 'ri-link'}`}></i>
              <span className="hidden sm:inline">{copied ? 'Copié !' : 'Inviter'}</span>
            </button>

            {isHost && !meetingEnded && (
              <button
                onClick={handleEndMeeting}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-stop-circle-line"></i>
                <span className="hidden sm:inline">Terminer</span>
              </button>
            )}

            {!isHost && (
              <button
                onClick={handleLeaveMeeting}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-xs font-medium text-white/80 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-logout-box-r-line"></i>
                <span className="hidden sm:inline">Quitter</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Zone vidéo + panneau participants */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        <div className="flex-1 min-w-0 relative flex flex-col overflow-hidden">
          <RoomAudioRenderer />
          <VideoGrid />
        </div>
        <div className="hidden md:flex">
          <LiveKitParticipantsBridge
            dbParticipants={dbParticipants}
            meeting={meeting}
            code={code}
            onCopyCode={handleCopyCode}
            copied={copied}
          />
        </div>
      </div>

      <div className="flex-shrink-0">
        <ControlBar
          variation="minimal"
          controls={{
            microphone: true,
            camera: true,
            screenShare: true,
            chat: false,
            leave: false,
          }}
        />
      </div>

      {showParticipantsMobile && (
        <>
          <div className="md:hidden fixed inset-0 bg-black/50 z-50" onClick={() => setShowParticipantsMobile(false)}></div>
          <div className="md:hidden fixed right-0 top-0 bottom-0 w-80 max-w-[85vw] z-50 flex flex-col shadow-2xl">
            <LiveKitParticipantsBridge
              dbParticipants={dbParticipants}
              meeting={meeting}
              code={code}
              onCopyCode={handleCopyCode}
              copied={copied}
              onClose={() => setShowParticipantsMobile(false)}
              isMobile
            />
          </div>
        </>
      )}
    </LiveKitRoom>
  );
}

function VideoGrid() {
  const allTracks = useTracks();

  if (!allTracks || allTracks.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-gray-900 min-h-0">
        <div className="w-16 h-16 rounded-2xl bg-white/10 flex items-center justify-center mb-4">
          <i className="ri-camera-line text-3xl text-white/40"></i>
        </div>
        <p className="text-white/60 text-sm">Initialisation de la vidéo...</p>
        <p className="text-white/30 text-xs mt-1">En attente des flux vidéo</p>
      </div>
    );
  }

  const screenShareTracks = allTracks.filter(
    (t) => t.source === Track.Source.ScreenShare || t.source === Track.Source.ScreenShareAudio,
  );
  const cameraTracks = allTracks.filter((t) => t.source === Track.Source.Camera);

  if (screenShareTracks.length > 0) {
    return (
      <div className="flex-1 flex flex-col min-h-0 bg-gray-900">
        <div className="flex-1 min-h-0 relative bg-black">
          <GridLayout tracks={screenShareTracks as TrackReference[]} className="h-full">
            <ParticipantTile />
          </GridLayout>
          <div className="absolute top-3 left-3 bg-clinic-green/90 text-white text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1.5 backdrop-blur-sm">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
            {screenShareTracks.some((t) => t.participant.isLocal) ? 'Vous partagez votre écran' : "Partage d'écran"}
          </div>
        </div>
        {cameraTracks.length > 0 && (
          <div className="flex-shrink-0 bg-gray-900/95 border-t border-white/10 px-3 py-2 overflow-x-auto max-h-[140px]">
            <div className="flex items-center gap-2" style={{ minWidth: 'fit-content', height: '110px' }}>
              {cameraTracks.map((track) => (
                <div
                  key={track.participant.identity + track.source}
                  className={`relative rounded-lg overflow-hidden flex-shrink-0 border-2 transition-colors ${
                    track.participant.isSpeaking ? 'border-clinic-green shadow-[0_0_8px_rgba(141,201,63,0.4)]' : 'border-white/10'
                  }`}
                  style={{ width: '160px', height: '110px' }}
                >
                  <ParticipantTile trackRef={track} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-hidden">
      <GridLayout tracks={cameraTracks as TrackReference[]} className="h-full">
        <ParticipantTile />
      </GridLayout>
    </div>
  );
}

function LiveKitParticipantsBridge({
  dbParticipants,
  meeting,
  code,
  onCopyCode,
  copied,
  onClose,
  isMobile,
}: {
  dbParticipants: DbParticipant[];
  meeting: MeetingData | null;
  code: string | undefined;
  onCopyCode: () => void;
  copied: boolean;
  onClose?: () => void;
  isMobile?: boolean;
}) {
  const livekitParticipants = useParticipants();
  return (
    <ParticipantsPanel
      dbParticipants={dbParticipants}
      livekitParticipants={livekitParticipants}
      meeting={meeting}
      code={code}
      onCopyCode={onCopyCode}
      copied={copied}
      onClose={onClose}
      isMobile={isMobile}
    />
  );
}